import { Link } from "react-router-dom";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SrIcon } from "@/components/ui/SrIcon";
import type { Offer, OfferStatus } from "@/hooks/use-offers";

const STATUS_OPTIONS: Array<{ value: OfferStatus; label: string }> = [
  { value: "draft", label: "Utkast" },
  { value: "sent", label: "Sendt" },
  { value: "accepted", label: "Akseptert" },
  { value: "lost", label: "Tapt" },
];

interface Props {
  offer: Offer;
  hasLines: boolean;
  hasProject: boolean;
  isExportingPptx: boolean;
  isDuplicating?: boolean;
  canSave: boolean;
  readOnly?: boolean;
  canManage?: boolean;
  onStatusChange: (status: OfferStatus) => void;
  onDelete: () => void;
  onExportPptx: () => void;
  onOpenSettings: () => void;
  onDuplicate?: () => void;
}

/**
 * Felles dropdown for mobil og desktop i tilbudseditoren.
 * Dupliser er tilgjengelig også i read-only (gir deg et utkast du eier).
 */
export function OfferEditMenu({
  offer,
  hasLines,
  hasProject,
  isExportingPptx,
  isDuplicating = false,
  canSave,
  readOnly,
  canManage = !readOnly,
  onStatusChange,
  onDelete,
  onExportPptx,
  onOpenSettings,
  onDuplicate,
}: Props) {
  return (
    <DropdownMenuContent align="end" className="w-56">
      {!readOnly && (
        <>
          <DropdownMenuItem onClick={onOpenSettings}>
            <SrIcon name="settings" className="w-3.5 h-3.5 mr-2" />
            Tilbudsinnstillinger
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {canManage && (
            <>
              {STATUS_OPTIONS.map((s) => (
                <DropdownMenuItem
                  key={s.value}
                  onClick={() => onStatusChange(s.value)}
                  disabled={s.value === offer.status}
                >
                  <SrIcon
                    name={s.value === offer.status ? "check" : "circle"}
                    className="w-3.5 h-3.5 mr-2 text-muted-foreground"
                  />
                  Status: {s.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}
        </>
      )}
      {onDuplicate && (
      <DropdownMenuItem
        onClick={onDuplicate}
        disabled={isDuplicating || canSave}
        title={
          canSave
            ? "Lagre tilbudet før du dupliserer"
            : "Lag et nytt utkast med samme linjer"
        }
      >
        <SrIcon name="copy" className="w-3.5 h-3.5 mr-2" />
        Dupliser tilbud
      </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={onExportPptx} disabled={isExportingPptx || !hasLines}>
        <SrIcon name="download" className="w-3.5 h-3.5 mr-2" />
        Last ned Keynote
      </DropdownMenuItem>
      {hasProject && offer.project_id && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={`/prosjekter/${offer.project_id}`}>
              <SrIcon name="folder-kanban" className="w-3.5 h-3.5 mr-2" />
              Gå til prosjekt
            </Link>
          </DropdownMenuItem>
        </>
      )}
      {canManage && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onDelete}
            className="text-destructive focus:text-destructive"
          >
            <SrIcon name="trash2" className="w-3.5 h-3.5 mr-2" />
            Slett tilbud
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  );
}
